import { useEffect } from "react";
import { Heart, Hand, Scale, BookOpen, Users, Crown, Check, ArrowRight, GraduationCap } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import Seo from "@/components/Seo";

const FOUNDER_PHOTO = "/images/founder-almeer-nance.jpg";

const PRINCIPLES = [
  { icon: Heart, title: "You Have Value", body: "Your past does not define your future. You have purpose and potential." },
  { icon: Hand, title: "Accountability Is Strength", body: "Owning your choices is the foundation for growth and freedom." },
  { icon: Scale, title: "Discipline Creates Freedom", body: "Self-control, consistency, and responsibility lead to a better life." },
  { icon: BookOpen, title: "Knowledge Is Power", body: "Education opens doors and builds confidence, critical thinking, and self-advocacy." },
  { icon: Users, title: "Community Builds Legacy", body: "When people in healing support one another, communities grow stronger and generations are lifted." },
  { icon: Crown, title: "Leadership Is Service", body: "True leaders uplift others, protect their communities, and leave a positive legacy." },
];

const ACKNOWLEDGE = [
  "Trauma",
  "Violence exposure",
  "Loss",
  "Systemic oppression",
  "Identity struggles",
  "Emotional suppression",
  "Economic hardship",
  "Generational cycles",
];

export default function About() {
  // Honor hash deep-links from the footer (e.g. /about#certification).
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const id = hash.replace("#", "");
    // Defer one frame so the section has rendered.
    const t = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
    return () => clearTimeout(t);
  }, [hash]);

  return (
    <div className="bg-tcpi-bg" data-testid="about-page">
      <Seo
        path="/about"
        title="About — Mission, Philosophy & Curriculum"
        description="Our mission, our trauma-informed philosophy, and the seven-track curriculum that serves justice-impacted men, women, and young people. Six core principles, dual pathways, and a path to certification."
      />
      {/* Intro */}
      <section className="relative py-24 md:py-32 grain">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="overline mb-6">About TCPI</div>
          <h1
            className="text-4xl md:text-6xl text-[#F7F5F0] leading-[1.1] mb-4"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
          >
            A transformational education, leadership, and rehabilitation curriculum.
          </h1>
          <p
            className="text-[#C9A961] mb-8 max-w-3xl text-sm md:text-base tracking-wide"
            data-testid="about-foundation-attribution"
          >
            The Crown Path Initiative is a program of <span className="font-semibold">The 1123 Foundation</span>.
          </p>
          <p className="text-lg text-[#D1CFC9] leading-relaxed mb-6 max-w-3xl">
            The Crown Path Initiative supports justice-impacted individuals — with dedicated
            pathways for <span className="text-[#C9A961]">men &amp; young men</span> and for{" "}
            <span className="text-[#C9A961]">women &amp; young girls</span> — through
            self-awareness, accountability, mental wellness, intellectual growth, and
            leadership development.
          </p>
          <p className="text-lg text-[#D1CFC9] leading-relaxed max-w-3xl">
            This curriculum acknowledges the unique experiences and challenges that
            justice-impacted people face — and provides a safe, structured, empowering
            environment where they can heal, grow, and become leaders who uplift others and
            transform their communities.
          </p>

          <div className="mt-12 card-bento max-w-2xl">
            <div className="overline mb-3">Our Promise</div>
            <p
              className="text-2xl text-[#F7F5F0] leading-snug"
              style={{ fontFamily: "Cormorant Garamond, serif" }}
            >
              "We see your potential. We respect your journey. We walk with you as you rebuild,
              rise, and reclaim your crown."
            </p>
          </div>
        </div>
      </section>

      {/* Core Philosophy */}
      <section className="bg-[#121110] py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <div className="overline mb-4">Our Core Philosophy</div>
            <h2 className="text-4xl md:text-5xl text-[#F7F5F0]" style={{ fontFamily: "Cormorant Garamond, serif" }}>
              Six principles. One foundation.
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PRINCIPLES.map(({ icon: Icon, title, body }) => (
              <div key={title} className="card-bento" data-testid={`principle-${title.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="w-12 h-12 mb-5 rounded-full border border-[#C9A961]/40 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-[#C9A961]" strokeWidth={1.6} />
                </div>
                <h3
                  className="text-2xl text-[#C9A961] mb-3"
                  style={{ fontFamily: "Cormorant Garamond, serif" }}
                >
                  {title}
                </h3>
                <p className="text-[#D1CFC9] leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Acknowledgement */}
      <section className="py-24 md:py-32 grain">
        <div className="max-w-5xl mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-16 items-start">
          <div>
            <div className="overline mb-4">We Acknowledge</div>
            <h2
              className="text-3xl md:text-4xl text-[#F7F5F0] mb-6"
              style={{ fontFamily: "Cormorant Garamond, serif" }}
            >
              Many of the people we serve carry burdens that are often unseen.
            </h2>
            <p className="text-[#D1CFC9] leading-relaxed">
              This program exists to break those cycles and build a new legacy. We do not deny
              what was done to you. We do not excuse what you have done. We work with the
              whole person — honestly, respectfully, and with the dignity earned by every step
              forward.
            </p>
          </div>
          <ul className="space-y-3">
            {ACKNOWLEDGE.map((item) => (
              <li key={item} className="flex items-center gap-3 text-[#F7F5F0]">
                <Check className="w-4 h-4 text-[#C9A961]" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Founder teaser */}
      <section className="py-24 md:py-28 grain" data-testid="about-founder-teaser">
        <div className="max-w-6xl mx-auto px-6 md:px-12 grid md:grid-cols-5 gap-12 items-center">
          <div className="md:col-span-2">
            <div className="relative">
              <div className="absolute -inset-3 bg-[#C9A961]/15 blur-2xl rounded-2xl" />
              <img
                src={FOUNDER_PHOTO}
                alt="Almeer Nance, founder of The Crown Path Initiative, in cap and gown"
                className="relative rounded-2xl border border-[#C9A961]/30 w-full object-cover"
              />
            </div>
          </div>
          <div className="md:col-span-3">
            <div className="overline mb-4">From the Founder</div>
            <h2
              className="text-3xl md:text-4xl text-[#F7F5F0] mb-5 leading-tight"
              style={{ fontFamily: "Cormorant Garamond, serif" }}
            >
              Almeer Nance built this from the inside out.
            </h2>
            <p className="text-[#D1CFC9] leading-relaxed mb-4">
              Sentenced as a juvenile to life plus 25 years in Tennessee, Almeer chose
              transformation over resignation. Across more than three decades inside, he earned
              college degrees, multiple vocational and leadership certifications, and became a
              mentor to younger incarcerated people through the YAOP program.
            </p>
            <p className="text-[#D1CFC9] leading-relaxed mb-6">
              The Crown Path Initiative is the curriculum he built from that journey — a
              structured pathway through emotional awareness, accountability, education, and
              leadership for justice-impacted individuals.
            </p>
            <Link
              to="/founder"
              className="btn-secondary inline-flex items-center gap-2"
              data-testid="about-founder-cta"
            >
              <GraduationCap className="w-4 h-4" /> Read his full story <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Certification Pathway */}
      <section id="certification" className="py-20 md:py-24 bg-[#121110]" data-testid="about-certification">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="overline mb-3">Certification Pathway</div>
          <h2
            className="text-3xl md:text-4xl text-[#F7F5F0] mb-5"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
          >
            Five tiers. One earned crown.
          </h2>
          <p className="text-[#D1CFC9] max-w-2xl mb-10 leading-relaxed">
            Progress through the seven tracks is recognized with five certification tiers.
            Each tier is earned through completed readings, qualified journal reflections,
            assessment scores, and substantive participation in Friday round-tables — not
            handed out for attendance.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {[
              { tier: "Candidate", desc: "Enrolled and active on at least one track." },
              { tier: "Scholar", desc: "Completed three tracks with qualified reflections." },
              { tier: "Mentor", desc: "Five tracks complete and supporting peers in Friday discussions." },
              { tier: "Ambassador", desc: "All seven tracks complete with strong assessment scores." },
              { tier: "Fellow", desc: "Sustained mentorship, leadership, and community contribution." },
            ].map((step, i) => (
              <div
                key={step.tier}
                className="p-5 rounded-md border border-[#C9A961]/25 bg-[#0A0908]/60"
                data-testid={`cert-tier-${i + 1}`}
              >
                <div className="overline text-[10px] text-[#C9A961] mb-2">Tier {i + 1}</div>
                <div className="text-lg text-[#F7F5F0] mb-2" style={{ fontFamily: "Cormorant Garamond, serif" }}>
                  {step.tier}
                </div>
                <p className="text-xs text-[#8A8782] leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing */}
      <section className="bg-[#121110] py-24 text-center">
        <p
          className="text-3xl md:text-4xl text-[#F7F5F0] max-w-3xl mx-auto px-6"
          style={{ fontFamily: "Cormorant Garamond, serif" }}
        >
          You are not your past.<br />
          <span className="text-[#C9A961] italic">You are your purpose.</span><br />
          And your crown is worth earning.
        </p>
      </section>

      {/* Support CTA */}
      <section className="py-24 grain relative">
        <div className="max-w-5xl mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="overline mb-4">Stand With Us</div>
            <h2
              className="text-3xl md:text-4xl text-[#F7F5F0] mb-5"
              style={{ fontFamily: "Cormorant Garamond, serif" }}
            >
              Fund a participant's path.<br />
              <span className="italic text-[#C9A961]">Partner with an institution.</span>
            </h2>
            <p className="text-[#D1CFC9] leading-relaxed mb-2">
              Every contribution sponsors workbook materials, facilitator hours, and reentry
              seats — the work of breaking generational cycles, one life at a time.
            </p>
          </div>
          <div className="space-y-3">
            <Link to="/support" className="btn-primary w-full flex items-center justify-center gap-2" data-testid="about-donate-cta">
              Donate to TCPI <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/support" className="btn-secondary w-full flex items-center justify-center gap-2" data-testid="about-license-cta">
              Institutional Partnership
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
